import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { outboundLogsService } from '../services/outboundLogsService';

const TRANSACTION_TTL_MS = 2 * 60 * 1000;

export const useTransactionStore = create(
  devtools(
    (set, get) => ({
      logs: [],
      summary: {},
      lastDocId: '',
      totalLogs: 0,
      loading: false,
      timeZone: '',
      lastFetched: 0,
      setTimeZone: (timeZone) => set({ timeZone }, false, 'transaction/setTimeZone'),
      loadTransactions: async ({ limit, force = false, resetCursor = false } = {}) => {
        const state = get();
        const timeZone = state.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const cacheFresh = Date.now() - state.lastFetched < TRANSACTION_TTL_MS;
        if (!force && state.logs.length && cacheFresh && !resetCursor) {
          return state.logs;
        }

        set({ loading: true }, false, 'transaction/load:start');
        try {
          const [summaryResponse, logsResponse] = await Promise.all([
            outboundLogsService.getSummary(),
            outboundLogsService.getLogs({
              timezone: timeZone,
              limit,
              lastDocId: resetCursor ? '' : state.lastDocId || '',
            }),
          ]);
          const summaryPayload = summaryResponse?.data?.summary || summaryResponse?.data?.data?.summary || {};
          const logsPayload = logsResponse?.data?.logs || logsResponse?.data?.data?.logs || [];
          const lastDocIdPayload = logsResponse?.data?.lastDocId || logsResponse?.data?.data?.lastDocId || '';
          const totalLogsPayload = logsResponse?.data?.totalLogs || logsResponse?.data?.data?.totalLogs || 0;
          set(
            {
              summary: summaryPayload,
              logs: logsPayload,
              lastDocId: lastDocIdPayload,
              totalLogs: totalLogsPayload,
              loading: false,
              lastFetched: Date.now(),
              timeZone,
            },
            false,
            'transaction/load:success'
          );
          return logsPayload;
        } catch (error) {
          set({ loading: false }, false, 'transaction/load:error');
          throw error;
        }
      },
      loadMoreTransactions: async ({ limit }) => {
        const state = get();
        const timeZone = state.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        set({ loading: true }, false, 'transaction/loadMore:start');
        try {
          const response = await outboundLogsService.getLogs({
            timezone: timeZone,
            limit,
            lastDocId: state.lastDocId || '',
          });
          const logsPayload = response?.data?.logs || response?.data?.data?.logs || [];
          const lastDocIdPayload = response?.data?.lastDocId || response?.data?.data?.lastDocId || state.lastDocId;
          const totalLogsPayload = response?.data?.totalLogs || response?.data?.data?.totalLogs || state.totalLogs;
          set(
            {
              logs: [...state.logs, ...logsPayload],
              lastDocId: lastDocIdPayload,
              totalLogs: totalLogsPayload,
              loading: false,
              lastFetched: Date.now(),
            },
            false,
            'transaction/loadMore:success'
          );
          return logsPayload;
        } catch (error) {
          set({ loading: false }, false, 'transaction/loadMore:error');
          throw error;
        }
      },
    }),
    { name: 'transaction-store' }
  )
);
